import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { codeSplit, DialogRoute, RouteStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { ErrorBoundary, Layer } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { VrInquiryChatbotViewModel } from 'models';

import { UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkToolbar } from 'uitk-react-toolbar';

import { ChatBotCard } from 'components/common/chat-bot-card';
import { ChatBotSurvey } from 'components/common/chat-bot-survey';
import { Conversations } from 'components/infosite/conversations';

const CodeSplitChatUIWrapper = codeSplit(() =>
  import(
    /* webpackChunkName: "chat-bot" */
    'homeaway-react-chat-ui-wrapper'
  )
) as any;

export interface ChatBotProps {
  context: Context;
  currentHotel?: CurrentHotelStore;
  routeStore?: RouteStore;
  model?: VrInquiryChatbotViewModel;
}

export interface ChatBotState {
  mounted: boolean;
  open: boolean;
}

@inject('context', 'currentHotel', 'routeStore')
@observer
export class ChatBot extends React.Component<ChatBotProps, ChatBotState> {
  constructor(props) {
    super(props);

    this.handleContactOwner = this.handleContactOwner.bind(this);
    this.closeContactOwner = this.closeContactOwner.bind(this);
    this.launchBot = this.launchBot.bind(this);
    this.handleCloseBot = this.handleCloseBot.bind(this);
    this.state = {
      mounted: false,
      open: false
    };
  }

  private handleContactOwner() {
    this.setState({
      open: false
    });

    if (this.props.routeStore) {
      this.props.routeStore.updateSearch({ pwaDialog: 'contact-owner' });
    }
  }

  private closeContactOwner() {
    if (this.props.routeStore) {
      this.props.routeStore.goBack();
    }

    this.setState({
      open: true
    });
  }

  private launchBot() {
    this.setState({
      open: true
    });
  }

  private handleCloseBot() {
    if (this.props.routeStore) {
      this.props.routeStore.updateSearch({ pwaDialog: 'exit-survey' });
    }
    this.setState({
      open: false
    });
  }

  public componentDidMount() {
    const { currentHotel } = this.props;

    if (currentHotel) {
      currentHotel.fetchConversations(String(currentHotel.hotelId));
    }

    this.setState({
      mounted: true
    });
  }

  public render() {
    const { context, currentHotel, model } = this.props;
    // Don't show if property doesn't allow inquiries OR on IE or no model
    if (
      !currentHotel ||
      !currentHotel.conversations ||
      !currentHotel.conversations.viewModel ||
      !currentHotel.conversations.viewModel.newConversation ||
      !context ||
      !context.deviceInformation ||
      context.deviceInformation.mobileBrowser === 'Internet Explorer'
    ) {
      return null;
    }

    const providerConfig = {
      attributes: {
        brand: context.site.brand,
        country: context.site.country,
        language: context.locale.substring(0, 2),
        listingId: String(currentHotel.hotelId),
        locale: context.locale,
        username: context.deviceId
      },
      brandName: context.site.name,
      labels: {
        botYouLabel: model.botYouLabel
      },
      tokenUrl: model.twilioTokenUrl
    };

    return (
      <ErrorBoundary label="hotel.information.chatbot">
        {this.state.mounted && (
          <CodeSplitChatUIWrapper
            i18n={{ ...model }}
            isOpen={this.state.open}
            onBookNowCallback={this.handleCloseBot}
            onCloseCallback={this.handleCloseBot}
            onContactOwnerCallback={this.handleContactOwner}
            providerConfig={providerConfig}
          />
        )}
        <ChatBotCard
          dialogTitle={model.dialogTitle}
          cardHeader={model.cardHeader}
          cardContent={model.cardContent}
          launchBot={this.launchBot}
        />
        <DialogRoute id="contact-owner" data-stid="chat-bot-contact">
          <Layer id="contact-owner-dialog" onDismiss={this.closeContactOwner}>
            <UitkFullscreenDialog dialogShow={true}>
              <UitkToolbar
                action={this.closeContactOwner}
                header={model.contactOwnerLabel}
                iconLabel={model.closeLabel}
                key={`toolbar-${currentHotel.hotelId}`}
              />
              <UitkDialogContent key={`content-${currentHotel.hotelId}`}>
                <Conversations show={true} />
              </UitkDialogContent>
            </UitkFullscreenDialog>
          </Layer>
        </DialogRoute>
        <ChatBotSurvey closeLabel={model.closeLabel} feedbackContent={model.feedbackContent} feedbackTitle={model.feedbackTitle} />
      </ErrorBoundary>
    );
  }
}
